import { useState } from "react";
import {
  Box, Paper, TextField, InputAdornment, Alert, Button, Chip,
} from "@mui/material";
import { PageHeader } from "@/components/PageHeader";
import { RowActions } from "@/components/RowActions";
import { DataGrid, type GridColDef, type GridPaginationModel, type GridRowParams } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { vendorsApi, type VendorDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { VendorForm } from "./VendorForm";

export function VendorsPage() {
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<VendorDto | null>(null);

  const canCreate = useAuth(s => s.hasPermission("procurement.vendor.create"));
  const canUpdate = useAuth(s => s.hasPermission("procurement.vendor.edit"));
  const canDelete = useAuth(s => s.hasPermission("procurement.vendor.delete"));
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["vendors", search, pagination.page, pagination.pageSize],
    queryFn: () => vendorsApi.list({
      search: search || undefined,
      page: pagination.page + 1,
      pageSize: pagination.pageSize,
    }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => vendorsApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["vendors"] }),
  });

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (v: VendorDto) => { setEditing(v); setFormOpen(true); };
  const handleDelete = (v: VendorDto) => {
    if (confirm(`Delete vendor "${v.name}"? This is blocked if any POs reference this vendor.`)) {
      deleteMutation.mutate(v.id);
    }
  };

  const columns: GridColDef<VendorDto>[] = [
    { field: "code", headerName: "Code", width: 120 },
    { field: "name", headerName: "Name", flex: 1, minWidth: 200 },
    { field: "contactPerson", headerName: "Contact", width: 160 },
    { field: "phone", headerName: "Phone", width: 130 },
    { field: "email", headerName: "Email", width: 200 },
    { field: "gstin", headerName: "GSTIN", width: 160 },
    { field: "creditDays", headerName: "Credit days", width: 110, type: "number" },
    {
      field: "isActive", headerName: "Status", width: 100,
      renderCell: (p) => (
        <Chip size="small" label={p.value ? "Active" : "Inactive"} color={p.value ? "success" : "default"} />
      ),
    },
    {
      field: "actions", headerName: "", width: 96, sortable: false, filterable: false,
      renderCell: (p) => (
        <RowActions actions={[
          { label: "Edit", icon: <EditIcon fontSize="small" />, hidden: !canUpdate, onClick: () => openEdit(p.row) },
          { label: "Delete", icon: <DeleteIcon fontSize="small" />, color: "error", hidden: !canDelete, onClick: () => handleDelete(p.row) },
        ]} />
      ),
    },
  ];

  return (
    <Box>
      <PageHeader breadcrumbs={["Catalog"]} title="Vendors"
        actions={<>{canCreate && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New Vendor
          </Button>
        )}</>}
      />

      <Paper sx={{ p: 2 }}>
        <TextField
          placeholder="Search by code or name"
          fullWidth size="small" value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ mb: 2 }}
        />
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        {deleteMutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(deleteMutation.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []}
            columns={columns}
            loading={query.isLoading}
            paginationMode="server"
            rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowDoubleClick={(p: GridRowParams<VendorDto>) => canUpdate && openEdit(p.row)}
          />
        </Box>
      </Paper>

      <VendorForm open={formOpen} onClose={() => setFormOpen(false)} vendor={editing} />
    </Box>
  );
}
